import type { ReactNode } from 'react'
import { AlertCircle, Info } from 'lucide-react'

type Tone = 'info' | 'error'

interface StatusScreenProps {
  readonly title: string
  readonly tone: Tone
  readonly detail?: string
  readonly hint?: string
  readonly action?: { readonly label: string; readonly onClick: () => void }
}

const toneStyles: Record<Tone, string> = {
  info: 'border-slate-200 bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100',
  error:
    'border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100',
}

/**
 * Ganzseitige Statusmeldung für Zwischenzustände.
 *
 * Bewusst gesprächig: Dieses Frontend wird von einer Person bedient, die
 * den Code nicht liest. Eine Meldung, die den nächsten Schritt nennt,
 * spart eine Rückfrage.
 */
export function StatusScreen({ title, tone, detail, hint, action }: StatusScreenProps): ReactNode {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 dark:bg-slate-900">
      <div className={`w-full max-w-lg rounded-lg border p-6 shadow-sm ${toneStyles[tone]}`}>
        <div className="flex items-center gap-2">
          {tone === 'error' ? (
            <AlertCircle aria-hidden="true" size={20} strokeWidth={1.8} />
          ) : (
            <Info aria-hidden="true" size={20} strokeWidth={1.8} />
          )}
          <h1 className="text-lg font-semibold">{title}</h1>
        </div>
        {detail !== undefined && <p className="mt-2 text-sm opacity-90">{detail}</p>}
        {hint !== undefined && (
          <p className="mt-4 rounded border border-current/20 bg-current/5 p-3 text-sm">{hint}</p>
        )}
        {action && (
          <button
            type="button"
            onClick={action.onClick}
            className="mt-5 inline-flex items-center gap-2 rounded bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
          >
            <Info aria-hidden="true" size={16} strokeWidth={2} />
            {action.label}
          </button>
        )}
      </div>
    </div>
  )
}
